import styled from "@emotion/styled";
import { ErrorMessage } from "@hookform/error-message";
import { yupResolver } from "@hookform/resolvers/yup";
import { Stack } from "@mui/material";
import { Button } from "components/UI";
import { MutationError } from "components/UI/MutationError";
import { useChurchStore } from "features/churches";
import {
  useBankStore,
  useChartOfAccounts,
  useCreateBank,
  useFinanceSettingsStore
} from "features/finances";
import { FILTER_OPERATIONS } from "features/members";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { ValidationError } from "../ValidationError";
import { BaseModal } from "./BaseModal";

const SmallText = styled.span`
  color: #8e8e8e;
  font-size: 10px;
  font-weight: 500;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Label = styled.label`
  color: #5b5b5b;
  font-size: 12px;
  font-weight: 500;
`;

const Input = styled.input`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
`;

const Textarea = styled.textarea`
  resize: none;
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  outline: none;
  padding: 8px;
  font-size: 12px;
  font-family: inherit;
`;

const Select = styled.select`
  border-radius: 4px;
  border: 0.73px solid #b3b3b3;
  height: 40px;
  outline: none;
  padding: 8px;
  background-color: transparent;
`;

const bankAccountSchema = yup.object().shape({
  name: yup.string().required("Bank name is required"),
  account_number: yup.string().required("Account number is required"),
  description: yup
    .string()
    .max(50, "Description must be less than 50 characters"),
  chart_of_account_id: yup.string().required("Chart of account is required")
});

export function CreateBankAccountModal() {
  const createBankAccountOpen = useBankStore(
    state => state.createBankAccountOpen
  );
  const closeCreateBankAccountModal = useBankStore(
    state => state.closeCreateBankAccountModal
  );
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const openCreateChartOfAccountModal = useFinanceSettingsStore(
    state => state.openCreateChartOfAccountModal
  );
  const setReturnToBankAccount = useFinanceSettingsStore(
    state => state.setReturnToBankAccount
  );

  const chartOfAccountsQuery = useChartOfAccounts({
    churchId: selectedChurch?.id,
    filters: [
      {
        column: "account_type",
        operator: FILTER_OPERATIONS.EQUAL,
        value: "ASSET"
      }
    ]
  });

  const createBankMutation = useCreateBank();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue
  } = useForm({
    defaultValues: {
      name: "",
      account_number: "",
      description: "",
      chart_of_account_id: ""
    },
    resolver: yupResolver(bankAccountSchema)
  });

  const onSubmit = data => {
    createBankMutation.mutate(
      {
        churchId: selectedChurch?.id,
        bankData: data
      },
      {
        onSuccess: () => {
          reset();
        }
      }
    );
  };

  const handleOpenChartOfAccount = () => {
    setReturnToBankAccount(true);
    closeCreateBankAccountModal();
    openCreateChartOfAccountModal();
  };

  return (
    <BaseModal
      title="Create bank account"
      isOpen={createBankAccountOpen}
      onClose={closeCreateBankAccountModal}
      footer={
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          <Button
            flavor="white"
            size="small"
            onClick={closeCreateBankAccountModal}
            disabled={createBankMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size="small"
            onClick={handleSubmit(onSubmit)}
            disabled={createBankMutation.isLoading}
          >
            Create bank account
          </Button>
        </Stack>
      }
    >
      <Field>
        <Label htmlFor="name">Bank Name</Label>
        <Input id="name" {...register("name")} />
        <ErrorMessage
          errors={errors}
          name="name"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="account_number">Bank Account Number</Label>
        <Input id="account_number" {...register("account_number")} />
        <ErrorMessage
          errors={errors}
          name="account_number"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="chart_of_account_id">Chart of Account</Label>
        <Select
          id="chart_of_account_id"
          {...register("chart_of_account_id")}
          onChange={e => {
            if (e.target.value === "create") {
              handleOpenChartOfAccount();
              setValue("chart_of_account_id", "");
            } else {
              setValue("chart_of_account_id", e.target.value);
            }
          }}
        >
          <option value="" disabled>
            Select chart of account
          </option>
          <option value="create">Create chart of account</option>
          {chartOfAccountsQuery.data?.result?.map(chartOfAccount => (
            <option key={chartOfAccount.id} value={chartOfAccount.id}>
              {chartOfAccount.account_name}
            </option>
          ))}
        </Select>
        <ErrorMessage
          errors={errors}
          name="chart_of_account_id"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <Field>
        <Label htmlFor="description">Description (optional)</Label>
        <Textarea id="description" rows={5} {...register("description")} />
        <ErrorMessage
          errors={errors}
          name="description"
          render={({ message }) => <ValidationError message={message} />}
        />
      </Field>
      <SmallText>No more than 50 characters</SmallText>

      <MutationError error={createBankMutation.error} sx={{ mt: 2 }} />
    </BaseModal>
  );
}
